/* The welcome code is activated only by a successful signup (or its saved timestamp).
 * Never send quantities, clear existing codes, or intercept a checkout action here.
 */
(function () {
  'use strict';

  var config = window.AvalyWelcomePromoConfig || {};
  var root = document.getElementById('avw-promo');
  if (!root || root.getAttribute('data-initialized') === 'true' || !config.code) return;
  var code = String(config.code).trim();
  var cartUrl;
  try {
    var route = new URL(config.cartUrl, location.origin);
    if (route.origin !== location.origin || !/\/cart\/?$/.test(route.pathname) || code.indexOf(',') !== -1) return;
    cartUrl = route.pathname.replace(/\/$/, '');
  } catch (error) { return; }
  root.setAttribute('data-initialized', 'true');

  function el(id) { return document.getElementById(id); }
  var toggle = el('avw-promo-toggle');
  var panel = el('avw-promo-panel');
  var codeText = el('avw-promo-code');
  var copy = el('avw-promo-copy');
  var copyStatus = el('avw-promo-copy-status');
  var status = el('avw-promo-status');
  var retry = el('avw-promo-retry');
  var refresh = el('avw-promo-refresh');
  if (!toggle || !panel || !codeText || !copy || !copyStatus || !status || !retry || !refresh) return;

  var YEAR = 365 * 864e5;
  var ATTEMPT_KEY = 'avw_promo_attempt';
  var active = false;
  var subscribedAt = 0;
  var timer = null;
  var running = false;
  var rerun = false;
  var rendering = false;
  var generation = 0;
  var observedLines = null;
  var observedDiscounts = null;
  var observedTotals = null;
  var waitingForCart = false;
  var unstableReads = 0;
  var pendingUntil = 0;
  var needsRefresh = false;
  var attempts = [];
  var mainCart = document.querySelector('[data-section-type="cart"][data-section-id]');
  var sectionId = mainCart && mainCart.getAttribute('data-section-id');
  var STATES = {
    checking: 'Checking your cart…',
    empty: 'Saved — add something to your cart and we’ll try it automatically.',
    applied: 'Welcome code applied to this cart.',
    ineligible: 'Saved — this code is not applicable to the current cart.',
    saved: 'Code saved. Copy it at checkout if you need it.',
    error: 'Code saved. Copy it at checkout, or try applying again.',
    protected: 'Code saved. Your existing discounts were left unchanged.'
  };

  function setState(state) {
    root.setAttribute('data-state', state);
    status.textContent = STATES[state];
    if (state === 'applied' && !refresh.hidden) status.textContent += ' Refresh cart totals to see the update.';
    retry.hidden = !/^(error|protected|saved)$/.test(state);
  }

  function sectionRefreshResult(updated) {
    refresh.hidden = updated;
    setState(root.getAttribute('data-state') || 'saved');
  }

  function validTimestamp(value) {
    var stamp = Number(value);
    return Number.isFinite(stamp) && stamp > 0 && stamp <= Date.now() && Date.now() - stamp < YEAR ? stamp : 0;
  }

  function subscriptionTimestamp() {
    var current = validTimestamp(config.subscribedAt);
    if (current) return current; // Also works for this page when storage is blocked.
    if (/[?&]avw=1(?:&|$)/.test(location.search)) return 0;
    try { return validTimestamp(localStorage.getItem('avw_sub')); } catch (error) { return 0; }
  }

  function normalized(value) { return String(value || '').trim().toUpperCase(); }

  /* Only a complete, explicit discount_codes array is safe to merge. Applied
   * allocations alone omit inapplicable codes and must not be used as a substitute.
   */
  function discountCodes(cart) {
    if (!Array.isArray(cart.discount_codes)) return null;
    var codes = [];
    for (var i = 0; i < cart.discount_codes.length; i++) {
      var entry = cart.discount_codes[i];
      var value = typeof entry === 'string' ? entry : entry && entry.code;
      if (typeof value !== 'string' || !value.trim() || value.indexOf(',') !== -1) return null;
      if (!codes.some(function (known) { return normalized(known) === normalized(value); })) codes.push(value.trim());
    }
    return codes;
  }

  function welcomeState(cart) {
    var entries = Array.isArray(cart.discount_codes) ? cart.discount_codes : [];
    for (var i = 0; i < entries.length; i++) {
      var entry = entries[i];
      if (normalized(typeof entry === 'string' ? entry : entry && entry.code) !== normalized(code)) continue;
      if (entry.applicable === true) return 'applied';
      if (entry.applicable === false) return 'ineligible';
      return 'saved';
    }
    return null;
  }

  /* Discount applications may change Shopify line keys. Quantities, variants,
   * selling plans and properties identify concurrent merchandise changes instead.
   */
  function lineFingerprint(cart) {
    return JSON.stringify((cart.items || []).map(function (item) {
      return [item.variant_id || item.id, Number(item.quantity), item.selling_plan_allocation &&
        item.selling_plan_allocation.selling_plan && item.selling_plan_allocation.selling_plan.id,
      Object.keys(item.properties || {}).sort().map(function (key) { return [key, item.properties[key]]; })];
    }).sort(function (a, b) { return JSON.stringify(a).localeCompare(JSON.stringify(b)); }));
  }

  function fingerprint(cart) {
    return JSON.stringify([lineFingerprint(cart), cart.currency, cart.original_total_price,
      (discountCodes(cart) || []).map(normalized).sort()]);
  }

  function discountFingerprint(cart) {
    if (!Array.isArray(cart.discount_codes)) return null; // Optimistic clones omit this field.
    return JSON.stringify(cart.discount_codes.map(function (entry) {
      return [normalized(typeof entry === 'string' ? entry : entry && entry.code), entry && entry.applicable];
    }).sort(function (a, b) { return a[0].localeCompare(b[0]); }));
  }

  function monetaryFingerprint(cart) {
    return JSON.stringify([cart.total_price, cart.original_total_price, cart.total_discount,
      (cart.cart_level_discount_applications || []).map(function (application) {
        return [application.type, application.title, application.total_allocated_amount];
      }), (cart.items || []).map(function (item) {
        return [item.variant_id || item.id, item.final_price, item.final_line_price,
          (item.line_level_discount_allocations || []).map(function (allocation) {
            return [allocation.amount, allocation.discount_application && allocation.discount_application.title];
          })];
      })]);
  }

  function rememberAttempt(before, after) {
    attempts = [before, after].filter(Boolean);
    try { sessionStorage.setItem(ATTEMPT_KEY, JSON.stringify({ at: Date.now(), subscription: subscribedAt, fingerprints: attempts })); } catch (error) {}
  }

  function requestJson(url, options) {
    options = options || {};
    options.credentials = 'same-origin';
    options.cache = 'no-store';
    return new Promise(function (resolve, reject) {
      var controller = window.AbortController ? new AbortController() : null;
      var settled = false;
      if (controller) options.signal = controller.signal;
      var timeout = setTimeout(function () {
        if (settled) return;
        settled = true;
        if (controller) controller.abort();
        reject(new Error('Welcome discount request timed out'));
      }, 8000);
      Promise.resolve().then(function () { return fetch(url, options); }).then(function (response) {
        if (!response.ok) throw new Error('Welcome discount HTTP ' + response.status);
        return response.json();
      }).then(function (body) {
        if (settled) return;
        settled = true; clearTimeout(timeout); resolve(body);
      }, function (error) {
        if (settled) return;
        settled = true; clearTimeout(timeout); reject(error);
      });
    });
  }

  function schedule(delay) {
    if (!active) return;
    clearTimeout(timer);
    timer = setTimeout(checkCart, delay == null ? 250 : delay);
  }

  function cartIsSettled(cart, version) {
    if (version !== generation || Date.now() < pendingUntil) return false;
    return !observedLines || observedLines === lineFingerprint(cart);
  }

  function waitForCart() {
    waitingForCart = true;
    setState('checking');
    if (unstableReads++ < 3) schedule(1000);
    else setState('error'); // Confirmed cart events or the Retry button can resume.
  }

  /* Request the server-rendered section, but only replace unbound monetary
   * fragments. Never replace forms, notes, quantity controls or payment buttons.
   */
  function updateMainCart(html) {
    if (!mainCart) return true;
    if (typeof html !== 'string' || !window.DOMParser) return false;
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var next = doc.querySelector('[data-section-type="cart"]');
    if (!next) return false;
    var oldRows = mainCart.querySelectorAll('.cart__item');
    var newRows = next.querySelectorAll('.cart__item');
    if (oldRows.length !== newRows.length) return false;
    for (var i = 0; i < oldRows.length; i++) {
      var oldQty = oldRows[i].querySelector('[data-quantity-input]');
      var newQty = newRows[i].querySelector('[data-quantity-input]');
      var oldTitle = oldRows[i].querySelector('.cart__item-title');
      var newTitle = newRows[i].querySelector('.cart__item-title');
      if (!oldQty || !newQty || oldQty.value !== newQty.value || !oldTitle || !newTitle ||
        oldTitle.getAttribute('href') !== newTitle.getAttribute('href')) return false;
    }
    function copyContents(oldRoot, newRoot, selector) {
      var oldNode = oldRoot.querySelector(selector);
      var newNode = newRoot.querySelector(selector);
      if (oldNode && newNode) oldNode.innerHTML = newNode.innerHTML;
    }
    for (var j = 0; j < oldRows.length; j++) {
      copyContents(oldRows[j], newRows[j], '.cart__item-price');
      copyContents(oldRows[j], newRows[j], '.cart__item-total');
      var oldDiscounts = oldRows[j].querySelector('.cart__line-item-discounts');
      var newDiscounts = newRows[j].querySelector('.cart__line-item-discounts');
      if (oldDiscounts) oldDiscounts.remove();
      if (newDiscounts) oldRows[j].querySelector('.cart__item-details').appendChild(newDiscounts.cloneNode(true));
    }
    var footer = mainCart.querySelector('.cart__footer-right');
    var newFooter = next.querySelector('.cart__footer-right');
    var total = footer && footer.querySelector('.ff-heading.fs-heading-4-base');
    if (!footer || !newFooter || !total) return false;
    ['.cart__original-total', '.cart__discounts'].forEach(function (selector) {
      var previous = footer.querySelector(selector);
      var replacement = newFooter.querySelector(selector);
      if (previous) previous.remove();
      if (replacement) footer.insertBefore(replacement.cloneNode(true), total);
    });
    copyContents(footer, newFooter, '.ff-heading.fs-heading-4-base');
    return true;
  }

  function renderCart(cart, version) {
    if (!cartIsSettled(cart, version)) { needsRefresh = true; waitForCart(); return; }
    rendering = true;
    try {
      if (window.AvalyCart && typeof window.AvalyCart.render === 'function') window.AvalyCart.render(cart, { emit: true });
    } finally { rendering = false; }
    observedLines = lineFingerprint(cart);
    observedDiscounts = discountFingerprint(cart);
    observedTotals = monetaryFingerprint(cart);
    needsRefresh = false;
    if (mainCart) {
      var html = cart.sections && cart.sections[sectionId];
      if (typeof html === 'string') sectionRefreshResult(updateMainCart(html));
      else {
        requestJson(cartUrl + '?sections=' + encodeURIComponent(sectionId)).then(function (sections) {
          if (version !== generation) return;
          sectionRefreshResult(updateMainCart(sections[sectionId]));
        }).catch(function () { if (version === generation) sectionRefreshResult(false); });
      }
    }
  }

  function checkCart() {
    if (!active) return;
    if (running) { rerun = true; return; }
    if (Date.now() < pendingUntil) { schedule(pendingUntil - Date.now() + 50); return; }
    running = true;
    var version = generation;
    requestJson(cartUrl + '.js').then(function (cart) {
      if (!cart || !Array.isArray(cart.items) || !Number.isFinite(cart.item_count)) throw new Error('Invalid cart response');
      if (!cartIsSettled(cart, version)) { waitForCart(); return; }
      waitingForCart = false; unstableReads = 0;
      var authoritativeTotals = monetaryFingerprint(cart);
      if (observedTotals !== null && observedTotals !== authoritativeTotals) needsRefresh = true;
      observedLines = lineFingerprint(cart);
      observedDiscounts = discountFingerprint(cart);
      observedTotals = authoritativeTotals;
      if (cart.item_count === 0) { setState('empty'); if (needsRefresh) renderCart(cart, version); return; }
      var existing = discountCodes(cart);
      if (!existing) { setState('protected'); return; }
      var current = welcomeState(cart);
      if (current) {
        setState(current);
        if (needsRefresh) renderCart(cart, version);
        return;
      }
      if (existing.length >= 5) { setState('protected'); return; }
      var before = fingerprint(cart);
      if (attempts.indexOf(before) !== -1) { setState('saved'); return; }
      rememberAttempt(before);
      setState('checking');
      var body = { discount: existing.concat([code]).join(',') };
      if (sectionId) { body.sections = [sectionId]; body.sections_url = location.pathname; }
      return requestJson(cartUrl + '/update.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify(body)
      }).then(function (updated) {
        if (!updated || !Array.isArray(updated.items) || !Number.isFinite(updated.item_count)) throw new Error('Invalid updated cart');
        rememberAttempt(before, fingerprint(updated));
        if (!cartIsSettled(updated, version)) { needsRefresh = true; waitForCart(); return; }
        var remaining = discountCodes(updated);
        var preserved = remaining && existing.every(function (known) {
          return remaining.some(function (value) { return normalized(value) === normalized(known); });
        });
        setState(preserved ? (welcomeState(updated) || 'error') : 'error');
        renderCart(updated, version);
      });
    }).catch(function () {
      if (version === generation) setState('error');
      else rerun = true;
    }).then(function () {
      running = false;
      if (rerun) { rerun = false; schedule(300); }
    });
  }

  function activate() {
    var stamp = subscriptionTimestamp();
    if (!stamp || /(^|\/)checkouts?(\/|$)/.test(location.pathname)) return;
    if (!active) {
      active = true; subscribedAt = stamp;
      codeText.textContent = code; root.hidden = false;
      if (mainCart) {
        var host = mainCart.querySelector('.cart__footer-left') || mainCart.querySelector('.cart__header');
        if (host) { host.appendChild(root); root.classList.add('avw-promo--cart'); }
      }
      try {
        var saved = JSON.parse(sessionStorage.getItem(ATTEMPT_KEY) || 'null');
        if (saved && saved.subscription === stamp && Date.now() - saved.at < 10 * 60e3 && Array.isArray(saved.fingerprints)) attempts = saved.fingerprints.slice(0, 2);
      } catch (error) {}
      setState('checking');
    }
    schedule(0);
  }

  function collapse() { panel.hidden = true; toggle.setAttribute('aria-expanded', 'false'); }
  toggle.addEventListener('click', function () {
    panel.hidden = !panel.hidden;
    toggle.setAttribute('aria-expanded', String(!panel.hidden));
  });
  root.addEventListener('keydown', function (event) {
    if (event.key === 'Escape' && !panel.hidden) { event.preventDefault(); collapse(); toggle.focus(); }
  });
  document.addEventListener('click', function (event) { if (!root.contains(event.target)) collapse(); });
  copy.addEventListener('click', function () {
    function fallback() {
      copyStatus.textContent = 'Select the code and copy it.';
      try {
        var range = document.createRange(); range.selectNodeContents(codeText);
        var selection = window.getSelection(); selection.removeAllRanges(); selection.addRange(range);
      } catch (error) {}
    }
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(code).then(function () {
        copy.textContent = 'Copied'; copyStatus.textContent = 'Welcome code copied.';
        setTimeout(function () { copy.textContent = 'Copy'; }, 1600);
      }, fallback);
    } else fallback();
  });
  retry.addEventListener('click', function () {
    attempts = []; unstableReads = 0;
    try { sessionStorage.removeItem(ATTEMPT_KEY); } catch (error) {}
    setState('checking'); schedule(0);
  });
  document.addEventListener('avaly:welcome-subscribed', activate);
  document.addEventListener('quick-cart:updated', function (event) {
    if (!active || rendering || !event.detail || !event.detail.cart) return;
    var next = lineFingerprint(event.detail.cart);
    var nextDiscounts = discountFingerprint(event.detail.cart);
    var nextTotals = monetaryFingerprint(event.detail.cart);
    var totalsChanged = observedTotals !== null && nextTotals !== observedTotals;
    if (next !== observedLines || (nextDiscounts !== null && nextDiscounts !== observedDiscounts) || totalsChanged) {
      // The cart controller can render a same-items clone without discount_codes.
      // Repair stale prices from a fresh GET, never from the event's clone itself.
      if (totalsChanged) needsRefresh = true;
      observedLines = next;
      if (nextDiscounts !== null) observedDiscounts = nextDiscounts;
      observedTotals = nextTotals;
      generation++; schedule(350);
    }
    else if (waitingForCart) schedule(350);
  });
  document.addEventListener('cart:item-added', function (event) {
    if (!active) return;
    generation++;
    if (event.detail && event.detail.optimistic === true) {
      pendingUntil = Date.now() + 8500;
      setState('checking'); schedule(8600); return;
    }
    pendingUntil = 0;
    if (event.detail && event.detail.cart) observedLines = lineFingerprint(event.detail.cart);
    schedule(350);
  });
  document.addEventListener('cart:error', function () {
    if (!active) return;
    pendingUntil = 0; observedLines = null; generation++; schedule(350);
  });
  /* Observe intent before the cart controller's capture listeners stop propagation.
   * This does not cancel, delay or change the customer's click or submission.
   */
  function cartIntent(event) {
    if (!active || !event.target || !event.target.closest) return;
    var target = event.target.closest('[data-avaly-increase],[data-avaly-decrease],[data-avaly-remove],button[data-increase],button[data-decrease],button[data-remove],button[data-add-to-cart],.avaly-quick-add__btn,button[data-quick-add][data-product-id],form[data-product-form]');
    if (!target) return;
    generation++; pendingUntil = Date.now() + 900; schedule(950);
  }
  window.addEventListener('click', cartIntent, true);
  window.addEventListener('submit', cartIntent, true);
  window.addEventListener('pageshow', function (event) { if (event.persisted && active) { generation++; observedLines = null; schedule(0); } });
  activate();
})();
